import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  connectedCallback() {
    super.connectedCallback();

    if (this.#controller.signal.aborted) this.#controller = new AbortController();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });

    if (this.dataset.mediaAreaAspectRatio && this.slideshow?.querySelector('slideshow-controls[thumbnails]')) {
      this.#thumbnailResizeObserver = new ResizeObserver(() => {
        cancelAnimationFrame(this.#thumbnailResizeFrame);
        this.#thumbnailResizeFrame = requestAnimationFrame(this.#resizeThumbnails);
      });
      this.#thumbnailResizeObserver.observe(this.slideshow);
    }
  }

  #controller = new AbortController();
  /** @type {ResizeObserver | undefined} */
  #thumbnailResizeObserver;
  #thumbnailResizeFrame = 0;

  #resizeThumbnails = () => {
    const slideshow = this.slideshow;
    const thumbnails = slideshow?.querySelector('.slideshow-controls__thumbnails');
    if (!slideshow || !thumbnails || thumbnails.getClientRects().length === 0) return;

    const galleryGap = parseFloat(getComputedStyle(slideshow).columnGap) || 0;
    const thumbnailGap = parseFloat(getComputedStyle(thumbnails).rowGap) || 0;
    const { width: areaWidth, height: areaHeight } = slideshow.getBoundingClientRect();
    if (areaWidth === 0 || areaHeight === 0) return;

    const thumbnailHeight = Math.max(0, (areaHeight - 3 * thumbnailGap) / 4);
    const mediaRatio = (areaWidth - galleryGap) / (areaHeight + thumbnailHeight);
    const width = thumbnailHeight * mediaRatio;

    const value = `${Math.max(0, width)}px`;
    if (this.style.getPropertyValue('--product-thumbnail-width') !== value) {
      this.style.setProperty('--product-thumbnail-width', value);
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
    this.#thumbnailResizeObserver?.disconnect();
    cancelAnimationFrame(this.#thumbnailResizeFrame);
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}
